<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {productCompare} from '../Store';
    import {compareGroup} from './compareSelection';

    const compare = productCompare();

    // Appears as just its header tab; only the customer opens it.
    const expanded = ref(false);

    // Every saved product stays in the list, by category and car: the group being compared first,
    // then the rest in the order they were ticked. The car is only named where one category was
    // ticked for two cars - otherwise the category alone already tells the groups apart.
    const groups = computed(() => {
        const byGroup = new Map<string, typeof compare.selected>();
        for (const item of compare.selected) byGroup.set(compareGroup(item), [...(byGroup.get(compareGroup(item)) ?? []), item]);
        const categories = [...byGroup.values()].map((items) => items[0].category);
        return [...byGroup]
            .map(([key, items]) => {
                const {category, car, typeCode} = items[0];
                const shared = categories.filter((other) => other === category).length > 1;
                // A page with a car code but no car of its own (search results) has no name to show.
                const carLabel = car || (typeCode ? 'andere auto' : 'zonder auto');
                const label = (category || 'Overig') + (shared ? ` · ${carLabel}` : '');
                return {key, label, items, active: key === compare.activeGroup};
            })
            .sort((a, b) => Number(b.active) - Number(a.active));
    });
    const activeLabel = computed(() => groups.value.find((group) => group.active)?.label ?? 'Producten');
    const comparable = computed(() => compare.activeItems.length >= 2);
</script>

<template>
    <div
        v-if="compare.selected.length > 0"
        class="compare-bar"
        :class="{'compare-bar--collapsed': !expanded}">
        <div class="compare-bar__header">
            <button
                type="button"
                class="compare-bar__toggle"
                :aria-expanded="expanded"
                @click="expanded = !expanded">
                <i
                    class="fa"
                    :class="expanded ? 'fa-chevron-down' : 'fa-chevron-up'"
                    aria-hidden="true" />
                <span class="compare-bar__label">{{ activeLabel }} vergelijken</span>
                <span class="compare-bar__count">{{ compare.activeItems.length }}</span>
            </button>

            <button
                v-if="expanded"
                type="button"
                class="compare-bar__clear"
                @click="compare.clear()">
                Wis alle
            </button>
        </div>

        <template v-if="expanded">
            <ul class="compare-bar__items">
                <template
                    v-for="group in groups"
                    :key="group.key">
                    <li
                        v-if="groups.length > 1"
                        class="compare-bar__group">
                        <template v-if="group.active">{{ group.label }} ({{ group.items.length }})</template>

                        <button
                            v-else
                            type="button"
                            class="compare-bar__switch"
                            @click="compare.chooseGroup(group.key)">
                            {{ group.label }} ({{ group.items.length }}) · vergelijk deze
                            <i
                                class="fa fa-chevron-right"
                                aria-hidden="true" />
                        </button>
                    </li>

                    <li
                        v-for="item in group.items"
                        :key="item.productId"
                        class="compare-bar__item"
                        :class="{'compare-bar__item--other': !group.active}">
                        <img
                            class="compare-bar__image"
                            :src="item.image"
                            alt="" />

                        <div class="compare-bar__copy">
                            <a
                                class="compare-bar__title"
                                :href="item.url">
                                {{ item.title }}
                            </a>

                            <div class="compare-bar__price">{{ item.price }}</div>
                        </div>

                        <button
                            type="button"
                            class="compare-bar__remove"
                            :aria-label="`${item.title} niet vergelijken`"
                            @click="compare.toggle(item)">
                            <i
                                class="fa fa-times"
                                aria-hidden="true" />
                        </button>
                    </li>
                </template>
            </ul>

            <div class="compare-bar__footer">
                <p
                    v-if="!comparable"
                    class="compare-bar__note">
                    Kies nog een product om te vergelijken.
                </p>

                <button
                    type="button"
                    class="mao-button black full-w"
                    :disabled="!comparable"
                    @click="compare.requestCompare()">
                    Bekijk vergelijking
                </button>
            </div>
        </template>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';

    /* Docked to the bottom of the screen, above the page but under the chat modal's backdrop (1040)
       and the mobile facet bar (3000). Colours are the shop's own sass/globals.scss tokens; its red has none. */
    .compare-bar {
        position: fixed;
        right: 16px;
        bottom: 0;
        z-index: 2147483551;
        width: 360px;
        max-width: calc(100vw - 32px);
        background: $white;
        border: 1px solid $grey;
        border-bottom: 0;
        border-radius: 4px 4px 0 0;
        box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.15);
        overflow: hidden;
    }

    .compare-bar--collapsed {
        width: auto;
    }

    .compare-bar__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 1px solid #c0c0c0;
        color: $black;
    }

    .compare-bar__toggle,
    .compare-bar__clear {
        border: 0;
        background: none;
        color: inherit;
        padding: 12px 16px;
        white-space: nowrap;
        /* The shop styles every button full-width. */
        width: auto;
    }

    /* The title and its count stay on one line, together, with "Wis alle" pushed to the edge. */
    .compare-bar__toggle {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 15px;
        font-weight: 700;
        text-align: left;
    }

    .compare-bar__toggle .fa {
        width: 12px;
    }

    /* A label that names the car ("Remblokset · Volkswagen GOLF VI (5K1)") can outgrow the bar;
       it is cut off rather than pushing the count out of sight. */
    .compare-bar__label {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .compare-bar__count {
        flex: 0 0 auto;
    }

    .compare-bar__clear {
        flex: 0 0 auto;
        font-size: 13px;
        text-decoration: none;
    }

    .compare-bar__count {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        border-radius: 10px;
        background: #e33b29;
        color: $white;
        font-size: 12px;
    }

    /* No focus ring left behind by a mouse click; keyboard focus keeps its outline. */
    .compare-bar__toggle:focus:not(:focus-visible),
    .compare-bar__clear:focus:not(:focus-visible) {
        outline: none;
    }

    .compare-bar__items {
        list-style: none;
        margin: 0;
        padding: 0 16px;
        max-height: 50vh;
        overflow-y: auto;
    }

    .compare-bar__item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 0;
    }

    .compare-bar__group {
        padding: 12px 0 0;
        color: $dark-grey;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
    }

    .compare-bar__switch {
        width: auto;
        border: 0;
        background: none;
        padding: 0;
        color: $link-blue;
        font-weight: 700;
        text-transform: uppercase;
    }

    .compare-bar__switch:hover {
        text-decoration: underline;
    }

    .compare-bar__switch:focus:not(:focus-visible) {
        outline: none;
    }

    .compare-bar__item--other {
        opacity: 0.6;
    }

    .compare-bar__item + .compare-bar__item {
        border-top: 1px solid $light-gray;
    }

    .compare-bar__image {
        flex: 0 0 48px;
        width: 48px;
        height: 48px;
        object-fit: contain;
    }

    .compare-bar__copy {
        flex: 1;
        min-width: 0;
    }

    /* Part titles run long (every OE number); two lines is enough to tell them apart. */
    .compare-bar__title {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        overflow: hidden;
        color: $black;
        font-size: 14px;
        font-weight: 700;
        line-height: 1.35;
    }

    .compare-bar__price {
        margin-top: 4px;
        color: #e33b29;
        font-weight: 700;
    }

    .compare-bar__remove {
        border: 0;
        background: none;
        padding: 0 0 0 4px;
        color: $dark-grey;
        font-size: 16px;
        line-height: 1.2;
        width: auto;
    }

    .compare-bar__remove:hover {
        color: $black;
    }

    .compare-bar__note {
        margin: 0 0 8px;
        color: $dark-grey;
        font-size: 13px;
    }

    .compare-bar__footer .mao-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .compare-bar__footer {
        padding: 12px 16px;
        border-top: 1px solid $light-gray;
    }
</style>
